import cssToObject from "css-to-object";
import _ from "lodash";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { compressHtml, compressNavTree } from "@/utils/navTreeCompression";
import { safeParseDocumentFromString } from "@/utils/safeHtmlParser/safeHtmlParser";
import { safeParseDocumentFromStringNonBlocking } from "@/utils/safeHtmlParser/safeHtmlParserNonBlocking";
import type { NavTreeNode, Storyboard } from "@/utils/types";

export const getFileKeyForStoryboard = (
  storyboard: Storyboard,
  canvasId: string,
) => {
  let fileKey = "";

  if (storyboard.fullFilePath) {
    fileKey = storyboard.fullFilePath.split(`${canvasId}/`)[1] || "";

    if (storyboard.type === STORYBOARD_TYPES.STORY) {
      fileKey += `/${storyboard.storyComponentName || storyboard.name}`;
    }
  }

  return fileKey;
};

export const getShellElementFromHtml = (doc: Document): HTMLElement | null => {
  const body = doc.body;
  if (body) {
    // Special case - for asset previews the wrapper elements are all known and can be discarded
    const assetRoot = doc.querySelector("[data-tempo-asset-container]");
    if (assetRoot) {
      return assetRoot as HTMLElement;
    }
    // Special cases - keep the react and nextjs root
    const root = doc.getElementById("root");
    if (root) {
      return root;
    }
    const next = doc.getElementById("__next");
    if (next) {
      return next;
    }

    return body;
  }

  return null;
};

export const getShellFromParsedHtml = (doc: Document) => {
  const shellElement = getShellElementFromHtml(doc);
  if (shellElement) {
    shellElement.innerHTML = "";
  }

  return doc.documentElement.outerHTML;
};

/**
 * Given an entire html document, return everything except what is inside the <body> tag
 */
export const getShellFromHtml = (html: string) => {
  // Use DOMParser for proper HTML parsing to avoid regex issues with nested content
  const doc = safeParseDocumentFromString(html);

  return getShellFromParsedHtml(doc);
};

/**
 * Regex pattern to match a body opening tag, properly handling > characters
 * inside quoted attribute values (e.g., <body onload="if(x>0){}">)
 *
 * Pattern breakdown:
 * - <body        : literal opening
 * - (?:          : non-capturing group for attributes, repeated
 *   [^>"']*      : any chars except >, ", '
 *   (?:"[^"]*"|'[^']*')?  : optionally a quoted string (which may contain >)
 * )*
 * - >            : closing bracket
 */
const BODY_OPEN_TAG_REGEX = /<body(?:[^>"']*(?:"[^"]*"|'[^']*')?)*>/i;

/**
 * Combines shell HTML (with empty body) and body HTML into a complete document.
 * Shell: <html><head>...</head><body></body></html>
 * Body: <body>...content...</body>
 * Result: <html><head>...</head><body>...content...</body></html>
 */
export const combineShellAndBody = (
  shellHtml: string,
  bodyHtml: string,
): string => {
  // Extract body content (without body tags)
  // First, find the opening body tag (handling > in quoted attributes)
  const bodyOpenMatch = bodyHtml.match(BODY_OPEN_TAG_REGEX);
  let bodyContent: string;

  if (bodyOpenMatch && bodyOpenMatch.index !== undefined) {
    const contentStart = bodyOpenMatch.index + bodyOpenMatch[0].length;
    // Find the last </body> tag (case-insensitive; handles </body> in scripts/comments)
    const closingBodyMatch = bodyHtml.match(/.*<\/body>/is);
    const closingBodyIndex = closingBodyMatch
      ? closingBodyMatch[0].length - "</body>".length
      : -1;
    if (closingBodyIndex >= contentStart) {
      bodyContent = bodyHtml.slice(contentStart, closingBodyIndex);
    } else {
      // No closing tag found, take everything after opening tag
      bodyContent = bodyHtml.slice(contentStart);
    }
  } else {
    // No body tag found, use as-is
    bodyContent = bodyHtml;
  }

  // Find the empty body in shell and insert content
  const shellBodyMatch = shellHtml.match(BODY_OPEN_TAG_REGEX);
  if (shellBodyMatch && shellBodyMatch.index !== undefined) {
    const insertIndex = shellBodyMatch.index + shellBodyMatch[0].length;
    return (
      shellHtml.slice(0, insertIndex) +
      bodyContent +
      shellHtml.slice(insertIndex)
    );
  }

  return shellHtml;
};

/**
 * Given an entire html document, return everything except what is inside the <body> tag
 */
export const getShellFromHtmlNonBlocking = async (html: string) => {
  // Use DOMParser for proper HTML parsing to avoid regex issues with nested content
  const doc = await safeParseDocumentFromStringNonBlocking(html);

  return getShellFromParsedHtml(doc);
};

/**
 * Given a parsed html document, return everything inside the <body> tag
 */
const getContentsWithoutShellFromParsedHtml = (doc: Document) => {
  const shellElement = getShellElementFromHtml(doc);
  if (shellElement) {
    return shellElement.innerHTML;
  }
  return "";
};

const convertClassToClassName = (html: string): string => {
  return html.replace(/ class=/g, " className=");
};

const removeComments = (html: string): string => {
  return html.replace(/<!--[\s\S]*?-->/g, "");
};

const imageFix = (html: string): string => {
  return html.replace(/<\/img>/g, "");
};

const convertStyleToObject = (html: string): string => {
  return html.replace(/style="(.*?)"/gi, (match) => {
    return `style={${JSON.stringify(cssToObject(match[1]))}}`;
  });
};

export const convertHtmlToJsx = (html: string) => {
  let newOuterHtml = convertClassToClassName(html);
  newOuterHtml = removeComments(newOuterHtml);
  newOuterHtml = imageFix(newOuterHtml);
  newOuterHtml = convertStyleToObject(newOuterHtml);
  return newOuterHtml;
};

/**
 * Given an entire html document, return everything inside the <body> tag
 */
export const getContentsWithoutShell = (
  html: string,
  filterOutScripts?: boolean,
) => {
  if (!html) {
    return "";
  }

  // Use DOMParser for proper HTML parsing to avoid regex issues with nested content
  const doc = safeParseDocumentFromString(html);

  if (filterOutScripts) {
    const scripts = Array.from(doc.getElementsByTagName("script"));
    for (const script of scripts) {
      script.remove();
    }
  }

  return getContentsWithoutShellFromParsedHtml(doc);
};

export const createInstantUpdateWithShell = (
  shellHtml: string,
  instantUpdateHtml: string,
  applyStylesToInstantUpdateHtml?: {
    [key: string]: string;
  },
) => {
  const doc = safeParseDocumentFromString(shellHtml);

  const applyStyles = (element: HTMLElement) => {
    if (applyStylesToInstantUpdateHtml) {
      Object.entries(applyStylesToInstantUpdateHtml).forEach(([key, value]) => {
        (element.children[0] as HTMLElement)?.style?.setProperty?.(key, value);
      });
    }
  };

  const body = doc.body;
  if (body) {
    const assetRoot = doc.querySelector("[data-tempo-asset-container]");
    if (assetRoot) {
      assetRoot.innerHTML = instantUpdateHtml;
      return doc.documentElement.innerHTML;
    }
    const root = doc.getElementById("root");
    if (root) {
      root.innerHTML = instantUpdateHtml;
      applyStyles(root);
      return doc.documentElement.outerHTML;
    }
    const next = doc.getElementById("__next");
    if (next) {
      next.innerHTML = instantUpdateHtml;
      applyStyles(next);
      return doc.documentElement.outerHTML;
    }
    body.innerHTML = instantUpdateHtml;
    applyStyles(body);
    return doc.documentElement.outerHTML;
  }

  return shellHtml;
};

/**
 * Detects if the html has a shell
 */
export const detectHtmlHasShell = (html: string): boolean => {
  if (!html || typeof html !== "string") {
    return false;
  }

  // Trim whitespace and normalize
  const trimmedHtml = html.trim();

  // Quick check: if it doesn't start with typical document markers, it's likely just content
  if (
    !trimmedHtml.startsWith("<!DOCTYPE") &&
    !trimmedHtml.startsWith("<html")
  ) {
    return false;
  }

  return true;
};

// Map to store throttled functions per storyboardId
const throttledFunctionsByStoryboardId = new Map<
  string,
  _.DebouncedFunc<any>
>();

const updateSavedHtmlOnDB = (
  storyboardId: string,
  canvasId: string,
  newOuterHtml: string,
  newNavTree?: NavTreeNode,
  skipUpdateNavTree?: boolean,
) => {
  // Frozen html should always just be the real changes on the iframe
  // Get the nav tree from the canvas store if not provided
  const compressedHtml = compressHtml(newOuterHtml);

  const newItem: any = {
    storyboard_id: storyboardId,
    html: compressedHtml,
    canvas_id: canvasId,
    last_frozen_at: new Date().toISOString(),
  };

  if (!skipUpdateNavTree) {
    const navTreeToSave = newNavTree || null;

    const compressedNavTree = compressNavTree(navTreeToSave);
    newItem.saved_nav_tree = compressedNavTree;
  }

  const supabaseClient = useSupabaseStore.getState().supabaseClient;
  if (!supabaseClient) {
    console.log("supabaseClient not found");
    return;
  }

  supabaseClient
    .from("saved_storyboard_html")
    .upsert(newItem, { onConflict: "storyboard_id" })
    .eq("storyboard_id", storyboardId)
    .then(() => {
      // Notify supabase store of the update
      useSupabaseStore
        .getState()
        .onDbSnapshot(
          "savedStoryboardHtml",
          { newItems: [newItem] },
          undefined,
          "storyboard_id",
        );
    });
};

export const throttledUpdateSavedHtmlOnDB = (
  storyboardId: string,
  canvasId: string,
  newOuterHtml: string,
  newNavTree?: NavTreeNode,
  skipUpdateNavTree?: boolean,
) => {
  // Get or create a throttled function for this specific storyboardId
  let throttledFunction = throttledFunctionsByStoryboardId.get(storyboardId);

  if (!throttledFunction) {
    throttledFunction = _.debounce(
      (
        canvasId: string,
        newOuterHtml: string,
        newNavTree?: NavTreeNode,
        skipUpdateNavTree?: boolean,
      ) => {
        updateSavedHtmlOnDB(
          storyboardId,
          canvasId,
          newOuterHtml,
          newNavTree,
          skipUpdateNavTree,
        );
      },
      3000,
      {
        maxWait: 60 * 1000,
      },
    );
    throttledFunctionsByStoryboardId.set(storyboardId, throttledFunction);
  }

  // Call the throttled function for this storyboardId
  throttledFunction(canvasId, newOuterHtml, newNavTree, skipUpdateNavTree);
};
